import { HttpClient } from '@angular/common/http';
import { api } from 'src/app/crypto-constants/api';
import { buildExternalAppUrl } from 'src/app/utils/app-route.util';
import {
  extractScytaleKycErrorMessage,
  formatScytaleKycUserMessage,
  isScytaleKycCreditLimitError,
  postScytaleKycWithRetry,
  SCYTALE_KYC_MAX_ATTEMPTS,
  ScytaleKycRetryOptions,
} from 'src/app/utils/scytale-kyc-retry.util';

export interface StandaloneScytaleSession {
  sessionUrl: string | null;
  sessionId: string | null;
}

/** Resolve SPA origin used in Scytale callback URLs. */
export function resolveStandaloneKycBaseUrl(clientBaseUrl?: string): string {
  const hostname = window.location.hostname.toLowerCase();

  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('localhost:') ||
    hostname.startsWith('127.0.0.1:')
  ) {
    return window.location.origin;
  }
  if (
    hostname.includes('dev') ||
    hostname.includes('dev.my-legacy.ai') ||
    hostname.includes('dev.clocr.com')
  ) {
    return (clientBaseUrl || 'https://dev.my-legacy.ai').replace(/\/$/, '');
  }
  if (
    hostname.includes('prod') ||
    hostname === 'my-legacy.ai' ||
    hostname.includes('prod.my-legacy.ai')
  ) {
    return 'https://my-legacy.ai';
  }
  return window.location.origin;
}

export function buildStandaloneKycCallbackUrl(
  profileRoute = '/profile/kyc-details',
  clientBaseUrl?: string
): string {
  const baseUrl = resolveStandaloneKycBaseUrl(clientBaseUrl);
  return `${buildExternalAppUrl(profileRoute, baseUrl)}?scytale=true`;
}

export function extractStandaloneScytaleSession(
  response: Record<string, unknown> | null | undefined
): StandaloneScytaleSession {
  if (!response) {
    return { sessionUrl: null, sessionId: null };
  }

  const kycData = response['kyc_data'] as Record<string, unknown> | undefined;
  const resultObject = kycData?.['resultObject'] as Record<string, unknown> | undefined;
  const data = response['data'] as Record<string, unknown> | undefined;

  const sessionUrl =
    asNonEmptyString(resultObject?.['sessionUrl']) ||
    asNonEmptyString(data?.['sessionUrl']) ||
    asNonEmptyString(data?.['url']) ||
    asNonEmptyString(response['sessionUrl']);

  const sessionId =
    asNonEmptyString(resultObject?.['sessionId']) ||
    asNonEmptyString(resultObject?.['sessionID']) ||
    asNonEmptyString(data?.['sessionId']) ||
    asNonEmptyString(data?.['sessionID']);

  return { sessionUrl, sessionId };
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export interface StandaloneKycErrorDisplay {
  /** Exact or mapped primary message (API text when available). */
  message: string;
  /** Optional guidance shown below the primary message on the KYC page. */
  detail?: string;
  /** Show link to Personal Details when profile data may be incomplete. */
  showProfileLink?: boolean;
}

export function isStandaloneKycStateNotFoundError(raw: string): boolean {
  return String(raw || '').toLowerCase().includes('state not found');
}

/** Primary + optional detail for inline KYC error UI. */
export function resolveStandaloneKycErrorDisplay(err: unknown): StandaloneKycErrorDisplay {
  if (isScytaleKycCreditLimitError(err)) {
    return { message: formatScytaleKycUserMessage(err) };
  }

  const raw = extractScytaleKycErrorMessage(err).trim();
  const lower = raw.toLowerCase();

  if (isStandaloneKycStateNotFoundError(raw)) {
    return {
      message: raw,
      detail:
        'Update your profile with your legal name, date of birth, and country, save your changes, ' +
        'then return here and tap Start KYC to continue.',
      showProfileLink: true,
    };
  }

  if (/^http failure response for /i.test(raw)) {
    return {
      message:
        'Unable to start KYC verification right now. Please try again in a few minutes. ' +
        'If this continues, contact support.',
    };
  }

  if (
    lower.includes('upstream connect error') ||
    lower.includes('remote connection failure') ||
    lower.includes('disconnect/reset before headers') ||
    lower.includes('bad gateway') ||
    lower.includes('gateway timeout') ||
    lower.includes('service unavailable')
  ) {
    return {
      message:
        'KYC provider is temporarily unavailable. Please wait a moment and try again. If this continues, contact support.',
    };
  }

  return { message: raw || 'Unable to start KYC verification. Please try again.' };
}

/** Single string for alert / toaster (primary + detail when present). */
export function formatStandaloneKycErrorMessage(err: unknown): string {
  const display = resolveStandaloneKycErrorDisplay(err);
  if (display.detail) {
    return `${display.message}\n\n${display.detail}`;
  }
  return display.message;
}

export interface StandaloneScytaleKycOptions
  extends Pick<ScytaleKycRetryOptions, 'onRetry' | 'maxAttempts' | 'baseDelayMs' | 'initialDelayMs'> {
  /** When true, Scytale revalidates against stored identity — no document upload. */
  isRecoveryFlow?: boolean;
}

export async function postStandaloneScytaleKyc(
  http: HttpClient,
  callbackUrl: string,
  options?: StandaloneScytaleKycOptions
): Promise<Record<string, unknown>> {
  const isRecoveryFlow = options?.isRecoveryFlow === true;
  const body: Record<string, unknown> = { callbackUrl };
  const params: Record<string, string> = {};
  if (isRecoveryFlow) {
    body['isRecoveryFlow'] = true;
    params['isRecoveryFlow'] = 'true';
  }

  return postScytaleKycWithRetry(http, api.azureTokenStandalone, body, {
    params: Object.keys(params).length ? params : undefined,
    maxAttempts: options?.maxAttempts ?? SCYTALE_KYC_MAX_ATTEMPTS,
    baseDelayMs: options?.baseDelayMs ?? 2000,
    initialDelayMs: options?.initialDelayMs ?? 750,
    logTag: '[StandaloneKYC] azure-token/standalone',
    onRetry: options?.onRetry,
  });
}

export async function redirectToStandaloneScytaleSession(
  sessionUrl: string,
  onWarning?: (message: string) => void
): Promise<void> {
  if (navigator.mediaDevices?.getUserMedia) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: true,
      });
      await new Promise((resolve) => setTimeout(resolve, 300));
      stream.getTracks().forEach((track) => track.stop());
      await new Promise((resolve) => setTimeout(resolve, 200));
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name ?? '';
      let errorMessage = 'Camera access is required for KYC verification. ';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera access in your browser settings and try again.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        errorMessage += 'No camera device found. Please connect a camera and try again.';
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        errorMessage += 'Camera is already in use by another application. Please close it and try again.';
      } else {
        errorMessage += 'Please check your camera settings and try again.';
      }
      onWarning?.(errorMessage);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  } else {
    onWarning?.(
      'Camera access is not available in this browser. Please use a modern browser with camera support.'
    );
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  window.location.href = sessionUrl;
}
